import React, { useEffect, useState, useRef } from 'react';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { socAPI, alertsAPI } from '@/lib/api';
import { cn, getSeverityColor, formatDate } from '@/lib/utils';
import {
  Shield, AlertTriangle, Clock, User, MessageSquare, Activity,
  CheckCircle, RefreshCw, Crosshair, Send, ChevronRight, Radio,
  Zap, Eye, XCircle, Flag, Target
} from 'lucide-react';
import { toast } from 'sonner';
import useAuthStore from '@/store/authStore';
import { useAlertWebSocket } from '@/hooks/useAlertWebSocket';

const getStatusColor = (status) => {
  const colors = {
    open: 'bg-destructive/20 text-destructive border-destructive/30',
    in_progress: 'bg-warning/20 text-warning border-warning/30',
    investigating: 'bg-info/20 text-info border-info/30',
    resolved: 'bg-success/20 text-success border-success/30',
    closed: 'bg-muted text-muted-foreground',
  };
  return colors[status] || 'bg-muted text-muted-foreground';
};

const getSlaStatus = (createdAt, slaHours) => {
  if (!createdAt) return { color: 'text-muted-foreground', label: '-' };
  const elapsed = (Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60);
  const pct = (elapsed / (slaHours || 4)) * 100;
  if (pct >= 100) return { color: 'text-destructive', label: 'SLA EXPIRADO', pct: 100 };
  if (pct >= 80) return { color: 'text-warning', label: `${Math.round(pct)}%`, pct };
  return { color: 'text-success', label: `${Math.round(pct)}%`, pct };
};

export function WarRoomPage() {
  const { user } = useAuthStore();
  const { isConnected, stats, lastEvent } = useAlertWebSocket();
  const [warRoomAlerts, setWarRoomAlerts] = useState([]);
  const [allAlerts, setAllAlerts] = useState([]);
  const [myQueue, setMyQueue] = useState([]);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [sendingNote, setSendingNote] = useState(false);
  const [activeTab, setActiveTab] = useState('warroom');
  const [liveEvents, setLiveEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const noteRef = useRef(null);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (lastEvent) {
      setLiveEvents(prev => [{ ...lastEvent, id: Date.now() }, ...prev].slice(0, 20));
      if (lastEvent.type === 'alert_updated' || lastEvent.type === 'alert_assigned' || lastEvent.type === 'war_room_update') {
        loadData();
      }
    }
  }, [lastEvent]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [warRes, allRes, queueRes] = await Promise.all([
        socAPI.getWarRoom(),
        alertsAPI.getAll({}),
        socAPI.getMyQueue(),
      ]);
      setWarRoomAlerts(warRes.data.alerts || []);
      setAllAlerts((allRes.data.alerts || []).filter(a => !['resolved','closed','false_positive'].includes(a.status)));
      setMyQueue(queueRes.data.alerts || []);
      if (selectedAlert) {
        const updated = [...(warRes.data.alerts || []), ...(allRes.data.alerts || [])].find(a => a.id === selectedAlert.id);
        if (updated) setSelectedAlert(updated);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssume = async (alertId) => {
    try {
      await socAPI.assignAlert(alertId);
      toast.success('Chamado assumido! Adicionado à sua fila.');
      loadData();
    } catch (e) { toast.error('Erro ao assumir chamado'); }
  };

  const handleInvestigate = async (alert) => {
    try {
      await socAPI.investigateAlert(alert.id);
      toast.success('Investigação iniciada — alerta na War Room');
      setSelectedAlert({ ...alert, in_war_room: true, status: 'investigating' });
      setActiveTab('warroom');
      loadData();
    } catch (e) { toast.error('Erro ao iniciar investigação'); }
  };

  const handleStatusChange = async (alertId, newStatus) => {
    try {
      await socAPI.updateAlert(alertId, { status: newStatus });
      toast.success(`Status alterado para ${newStatus}`);
      loadData();
    } catch (e) { toast.error('Erro ao alterar status'); }
  };

  const handleSeverityChange = async (alertId, severity) => {
    try {
      await socAPI.updateAlert(alertId, { severity });
      toast.success(`Severidade alterada para ${severity}`);
      loadData();
    } catch (e) { toast.error('Erro ao alterar severidade'); }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedAlert) return;
    setSendingNote(true);
    try {
      await socAPI.addNote(selectedAlert.id, newNote);
      toast.success('Nota adicionada ao caso');
      setNewNote('');
      loadData();
    } catch (e) { toast.error('Erro ao adicionar nota'); }
    finally { setSendingNote(false); }
  };

  const renderAlertList = (alerts, showInvestigate = true) => (
    <div className="space-y-2">
      {alerts.length === 0 && (
        <div className="text-center py-8 text-muted-foreground text-sm">Nenhum item</div>
      )}
      {alerts.map(alert => {
        const sla = getSlaStatus(alert.created_at, alert.sla_hours);
        return (
          <div
            key={alert.id}
            className={cn(
              "p-3 rounded-sm border cursor-pointer transition-all hover:border-primary/50",
              selectedAlert?.id === alert.id ? "border-primary bg-primary/10" : "border-border bg-secondary/30"
            )}
            onClick={() => setSelectedAlert(alert)}
            data-testid={`war-room-alert-${alert.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Badge className={cn("border text-xs flex-shrink-0", getSeverityColor(alert.severity))}>
                    {alert.severity?.toUpperCase()}
                  </Badge>
                  <Badge className={cn("border text-xs flex-shrink-0", getStatusColor(alert.status))}>
                    {alert.status?.replace('_', ' ').toUpperCase()}
                  </Badge>
                </div>
                <p className="text-sm font-medium truncate">{alert.title}</p>
                {alert.assigned_to && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                    <User className="h-3 w-3" />{alert.assigned_to}
                  </p>
                )}
              </div>
              <div className="text-right flex-shrink-0">
                <p className={cn("text-xs font-mono", sla.color)}>{sla.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">SLA</p>
              </div>
            </div>
            {showInvestigate && alert.status === 'open' && (
              <div className="flex gap-2 mt-2" onClick={e => e.stopPropagation()}>
                <Button size="sm" variant="outline" className="flex-1 h-7 text-xs" onClick={() => handleAssume(alert.id)}>
                  <User className="h-3 w-3 mr-1" />Assumir
                </Button>
                <Button size="sm" className="flex-1 h-7 text-xs bg-warning/20 hover:bg-warning/30 text-warning border border-warning/30" onClick={() => handleInvestigate(alert)}>
                  <Target className="h-3 w-3 mr-1" />Investigar
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="war-room-page">
      <Header title="War Room — SOC Operations" />

      {/* Status Bar */}
      <div className="border-b border-border bg-card/80 px-6 py-2 flex items-center gap-4 text-xs">
        <div className={cn("flex items-center gap-1.5", isConnected ? "text-success" : "text-destructive")}>
          <Radio className="h-3 w-3 animate-pulse" />
          <span>{isConnected ? 'LIVE — CONECTADO' : 'OFFLINE'}</span>
        </div>
        <div className="flex items-center gap-1.5 text-destructive">
          <AlertTriangle className="h-3 w-3" />
          <span>{stats.critical} CRÍTICOS EM ABERTO</span>
        </div>
        <div className="flex items-center gap-1.5 text-warning">
          <Activity className="h-3 w-3" />
          <span>{warRoomAlerts.length} EM INVESTIGAÇÃO</span>
        </div>
        <div className="flex items-center gap-1.5 text-info ml-auto">
          <Clock className="h-3 w-3" />
          <span>{new Date().toLocaleTimeString('pt-BR')}</span>
        </div>
        <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={loadData}>
          <RefreshCw className="h-3 w-3 mr-1" />Atualizar
        </Button>
      </div>

      <div className="flex h-[calc(100vh-112px)]">
        {/* Left Panel — Alert Queues */}
        <div className="w-80 border-r border-border flex flex-col flex-shrink-0">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full">
            <TabsList className="w-full rounded-none border-b border-border bg-card/50 h-9">
              <TabsTrigger value="warroom" className="flex-1 text-xs">
                <Crosshair className="h-3 w-3 mr-1" />War Room ({warRoomAlerts.length})
              </TabsTrigger>
              <TabsTrigger value="queue" className="flex-1 text-xs">
                <User className="h-3 w-3 mr-1" />Minha Fila ({myQueue.length})
              </TabsTrigger>
              <TabsTrigger value="open" className="flex-1 text-xs">
                <Flag className="h-3 w-3 mr-1" />Geral ({allAlerts.length})
              </TabsTrigger>
            </TabsList>

            <ScrollArea className="flex-1">
              <div className="p-3">
                <TabsContent value="warroom" className="mt-0">
                  {renderAlertList(warRoomAlerts, false)}
                </TabsContent>
                <TabsContent value="queue" className="mt-0">
                  {renderAlertList(myQueue)}
                </TabsContent>
                <TabsContent value="open" className="mt-0">
                  {renderAlertList(allAlerts)}
                </TabsContent>
              </div>
            </ScrollArea>
          </Tabs>
        </div>

        {/* Main Panel — Investigation */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {selectedAlert ? (
            <div className="flex h-full">
              {/* Investigation Details */}
              <div className="flex-1 flex flex-col overflow-hidden border-r border-border">
                {/* Alert Header */}
                <div className="p-4 border-b border-border bg-card/50 flex-shrink-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Badge className={cn("border", getSeverityColor(selectedAlert.severity))}>
                          {selectedAlert.severity?.toUpperCase()}
                        </Badge>
                        <Badge className={cn("border", getStatusColor(selectedAlert.status))}>
                          {selectedAlert.status?.replace('_', ' ').toUpperCase()}
                        </Badge>
                        {selectedAlert.in_war_room && (
                          <Badge className="bg-primary/20 text-primary border-primary/30 border">
                            <Crosshair className="h-3 w-3 mr-1" />WAR ROOM
                          </Badge>
                        )}
                        {selectedAlert.assigned_to && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <User className="h-3 w-3" />{selectedAlert.assigned_to}
                          </span>
                        )}
                      </div>
                      <h2 className="font-medium">{selectedAlert.title}</h2>
                      <p className="text-sm text-muted-foreground mt-1">{selectedAlert.description}</p>
                    </div>

                    {/* Quick Actions */}
                    <div className="flex gap-2 ml-4 flex-shrink-0">
                      {!selectedAlert.assigned_to && (
                        <Button size="sm" variant="outline" onClick={() => handleAssume(selectedAlert.id)}>
                          <User className="h-4 w-4 mr-1" />Assumir
                        </Button>
                      )}
                      {!selectedAlert.in_war_room && (
                        <Button size="sm" className="bg-warning/20 text-warning border border-warning/30 hover:bg-warning/30" onClick={() => handleInvestigate(selectedAlert)}>
                          <Target className="h-4 w-4 mr-1" />Investigar
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Change Controls */}
                  <div className="flex gap-3 mt-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Severidade:</span>
                      <Select value={selectedAlert.severity} onValueChange={(v) => handleSeverityChange(selectedAlert.id, v)}>
                        <SelectTrigger className="h-7 w-[110px] text-xs" data-testid="severity-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {['critical','high','medium','low','info'].map(s => (
                            <SelectItem key={s} value={s}>{s.toUpperCase()}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Status:</span>
                      <Select value={selectedAlert.status} onValueChange={(v) => handleStatusChange(selectedAlert.id, v)}>
                        <SelectTrigger className="h-7 w-[130px] text-xs" data-testid="status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[['open','Aberto'],['in_progress','Em Análise'],['investigating','Investigando'],['resolved','Resolvido'],['closed','Fechado']].map(([v,l]) => (
                            <SelectItem key={v} value={v}>{l}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-hidden">
                  <Tabs defaultValue="details" className="h-full flex flex-col">
                    <TabsList className="mx-4 mt-3 bg-secondary/50 flex-shrink-0">
                      <TabsTrigger value="details" className="text-xs">Detalhes</TabsTrigger>
                      <TabsTrigger value="timeline" className="text-xs">
                        Timeline ({(selectedAlert.timeline || []).length})
                      </TabsTrigger>
                      <TabsTrigger value="notes" className="text-xs">
                        Notas ({(selectedAlert.case_notes || []).length})
                      </TabsTrigger>
                    </TabsList>

                    <ScrollArea className="flex-1 mt-3">
                      <TabsContent value="details" className="px-4 pb-4 mt-0 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <Card className="bg-secondary/30">
                            <CardContent className="pt-3 pb-3">
                              <h4 className="font-display text-xs uppercase text-muted-foreground mb-2">Rede</h4>
                              <div className="space-y-1 text-sm">
                                <div className="flex justify-between"><span className="text-muted-foreground">IP Origem</span><span className="font-mono">{selectedAlert.source_ip || '-'}</span></div>
                                <div className="flex justify-between"><span className="text-muted-foreground">IP Destino</span><span className="font-mono">{selectedAlert.dest_ip || '-'}</span></div>
                                <div className="flex justify-between"><span className="text-muted-foreground">País</span><span>{selectedAlert.source_country || '-'}</span></div>
                              </div>
                            </CardContent>
                          </Card>
                          <Card className="bg-secondary/30">
                            <CardContent className="pt-3 pb-3">
                              <h4 className="font-display text-xs uppercase text-muted-foreground mb-2">MITRE ATT&CK</h4>
                              <div className="space-y-1 text-sm">
                                <div className="flex justify-between"><span className="text-muted-foreground">Técnica</span><Badge variant="outline" className="font-mono text-xs">{selectedAlert.mitre_technique || '-'}</Badge></div>
                                <div className="flex justify-between"><span className="text-muted-foreground">Fonte</span><span>{selectedAlert.source || '-'}</span></div>
                                <div className="flex justify-between"><span className="text-muted-foreground">Aberto em</span><span className="text-xs">{selectedAlert.created_at?.substring(0,10)}</span></div>
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                        {selectedAlert.assigned_to && (
                          <Card className="bg-primary/5 border-primary/20">
                            <CardContent className="pt-3 pb-3">
                              <h4 className="font-display text-xs uppercase text-primary mb-2">Responsável</h4>
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
                                  {selectedAlert.assigned_to?.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <p className="font-medium text-sm">{selectedAlert.assigned_to}</p>
                                  <p className="text-xs text-muted-foreground">Assumiu em {selectedAlert.assigned_at?.substring(0,16)?.replace('T',' ')}</p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </TabsContent>

                      <TabsContent value="timeline" className="px-4 pb-4 mt-0">
                        <div className="space-y-3">
                          {(selectedAlert.timeline || []).length === 0 && (
                            <p className="text-sm text-muted-foreground text-center py-4">Nenhuma ação registrada ainda</p>
                          )}
                          {(selectedAlert.timeline || []).map((event, i) => (
                            <div key={i} className="flex gap-3">
                              <div className="flex flex-col items-center">
                                <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                                  {event.type === 'status_change' && <Activity className="h-3 w-3 text-info" />}
                                  {event.type === 'severity_change' && <AlertTriangle className="h-3 w-3 text-warning" />}
                                  {event.type === 'assigned' && <User className="h-3 w-3 text-success" />}
                                  {event.type === 'investigation_started' && <Target className="h-3 w-3 text-primary" />}
                                  {event.type === 'note_added' && <MessageSquare className="h-3 w-3 text-muted-foreground" />}
                                </div>
                                {i < (selectedAlert.timeline || []).length - 1 && <div className="w-px flex-1 bg-border mt-1" />}
                              </div>
                              <div className="flex-1 pb-3">
                                <div className="flex items-center justify-between">
                                  <p className="text-sm font-medium">
                                    {event.type === 'status_change' && `Status: ${event.from} → ${event.to}`}
                                    {event.type === 'severity_change' && `Severidade: ${event.from} → ${event.to}`}
                                    {event.type === 'assigned' && `Chamado assumido`}
                                    {event.type === 'investigation_started' && `Investigação iniciada`}
                                    {event.type === 'note_added' && `Nota adicionada`}
                                  </p>
                                  <span className="text-xs text-muted-foreground">{event.timestamp?.substring(0,16)?.replace('T',' ')}</span>
                                </div>
                                <p className="text-xs text-muted-foreground">por {event.user}</p>
                                {event.type === 'note_added' && event.note && (
                                  <p className="text-xs bg-secondary/50 px-2 py-1 rounded mt-1 italic">"{event.note}"</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </TabsContent>

                      <TabsContent value="notes" className="px-4 pb-4 mt-0 space-y-3">
                        {(selectedAlert.case_notes || []).map((note) => (
                          <Card key={note.id} className="bg-secondary/30">
                            <CardContent className="pt-3 pb-3">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs font-bold">
                                  {note.user?.charAt(0).toUpperCase()}
                                </div>
                                <span className="text-sm font-medium">{note.user}</span>
                                <span className="text-xs text-muted-foreground ml-auto">{note.timestamp?.substring(0,16)?.replace('T',' ')}</span>
                              </div>
                              <p className="text-sm">{note.note}</p>
                            </CardContent>
                          </Card>
                        ))}

                        <div className="flex gap-2 sticky bottom-0">
                          <Textarea
                            ref={noteRef}
                            placeholder="Adicionar nota ao caso... (ex: verifiquei o log do firewall, origem é IP conhecido de botnet)"
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            className="flex-1 min-h-[80px] text-sm resize-none"
                            data-testid="case-note-input"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && e.ctrlKey) handleAddNote();
                            }}
                          />
                          <Button className="self-end" size="sm" onClick={handleAddNote} disabled={sendingNote || !newNote.trim()} data-testid="add-note-btn">
                            {sendingNote ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                          </Button>
                        </div>
                      </TabsContent>
                    </ScrollArea>
                  </Tabs>
                </div>
              </div>

              {/* Right Panel — Live Feed */}
              <div className="w-64 flex flex-col bg-card/30 flex-shrink-0">
                <div className="p-3 border-b border-border">
                  <h3 className="font-display text-xs uppercase text-muted-foreground flex items-center gap-2">
                    <Radio className="h-3 w-3 text-success animate-pulse" />
                    Feed ao Vivo
                  </h3>
                </div>
                <ScrollArea className="flex-1">
                  <div className="p-2 space-y-2">
                    {liveEvents.length === 0 && (
                      <p className="text-xs text-muted-foreground text-center py-4">Aguardando eventos...</p>
                    )}
                    {liveEvents.map((event) => (
                      <div key={event.id} className="p-2 bg-secondary/30 rounded text-xs border-l-2 border-primary/50">
                        <p className="text-primary font-mono">{event.type?.replace('_', ' ').toUpperCase()}</p>
                        <p className="text-muted-foreground mt-0.5">{event.timestamp?.substring(0,16)?.replace('T',' ')}</p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center space-y-4">
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <Crosshair className="h-10 w-10 text-primary opacity-50" />
                </div>
                <div>
                  <p className="text-lg font-display uppercase">War Room Pronta</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Selecione um alerta ou clique em "Investigar" para iniciar uma investigação crítica
                  </p>
                </div>
                <div className="flex gap-4 justify-center text-sm">
                  <div className="text-center">
                    <p className="text-2xl font-mono font-bold text-destructive">{stats.critical}</p>
                    <p className="text-muted-foreground">Críticos</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-mono font-bold text-warning">{myQueue.length}</p>
                    <p className="text-muted-foreground">Minha fila</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-mono font-bold text-info">{warRoomAlerts.length}</p>
                    <p className="text-muted-foreground">Em investigação</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default WarRoomPage;
